import React, { useState, useEffect } from "react";
import { analyticsRouteWiseBusOccupancy } from "../../services/AnalyticService";
import { Spinner } from "react-bootstrap";
import { dateRangeOptions } from "../../utilities";
import Chart from "react-apexcharts";
import {
  addDays,
  format,
  subDays,
  startOfMonth,
  endOfMonth,
  subMonths,
} from "date-fns";
import DatePicker from "react-datepicker";
import Select from "react-select";
const RoutewiseBusOccupancy = () => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedOption, setSelectedOption] = useState(dateRangeOptions[1]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [param, setParam] = useState({
    start_date: format(subDays(new Date(), 7), "yyyy-MM-dd"),
    end_date: format(new Date(), "yyyy-MM-dd"),
  });
  useEffect(() => {
    setLoading(true);
    analyticsRouteWiseBusOccupancy(param)
      .then((res) => {
        setData(res?.data?.data || []);
        setError("");
      })
      .catch((err) => {
        setError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Try again later"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [param]);
  const handleSelectChange = (option) => {
    setSelectedOption(option);
    if (!option) {
      setParam({ ...param, start_date: "", end_date: "", page: "1" });
      return;
    }
    let startDate,
      endDate = new Date();
    switch (option.value) {
      case "yesterday":
        startDate = subDays(new Date(), 1);
        endDate = startDate;
        break;
      case "7_days":
        startDate = subDays(new Date(), 7);
        break;
      case "15_days":
        startDate = subDays(new Date(), 15);
        break;
      case "30_days":
        startDate = subDays(new Date(), 30);
        break;
      case "last_month":
        startDate = startOfMonth(subMonths(new Date(), 1));
        endDate = endOfMonth(subMonths(new Date(), 1));
        break;
      case "3_months":
        startDate = startOfMonth(subMonths(new Date(), 3));
        endDate = endOfMonth(subMonths(new Date(), 1));
        break;
      case "custom":
        setShowDatePicker(true);
        return;
      default:
        return;
    }
    setShowDatePicker(false);
    setParam({
      ...param,
      start_date: format(startDate, "yyyy-MM-dd"),
      end_date: format(endDate, "yyyy-MM-dd"),
      page: "1",
    });
  };
  const series = data.map((item) => item.occupancy);
  const labels = data.map((item) => item.route);

  // Dynamically generate attractive chocolate-toned colors
  const generateColors = (count) => {
    const saturation = 70; // Rich saturation
    const lightness = 45; // Balanced brightness
    const hueStep = 360 / count; // Distribute evenly around color wheel

    return Array.from({ length: count }, (_, i) => {
      const hue = Math.round((30 + i * hueStep) % 360); // Start from 30° (chocolate), wrap around
      return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    });
  };

  const options = {
    chart: {
      type: "polarArea",
    },
    labels: labels,
    colors: generateColors(data.length),
    stroke: {
      colors: ["#fff"],
    },
    fill: {
      opacity: 0.85,
    },
    legend: {
      position: "right",
      formatter: function (seriesName, opts) {
        const label = opts.w.globals.labels[opts.seriesIndex];
        return label.length > 20 ? label.substring(0, 20) + "..." : label;
      },
    },
    tooltip: {
      custom: function ({ series, seriesIndex, w }) {
        const fullLabel = w.globals.labels[seriesIndex];
        const value = series[seriesIndex];
        return `
        <div class="apex-tooltip-custom" style="
          background-color: #3e2723;
          color: #fff;
          padding: 10px 12px;
          border-radius: 8px;
          font-size: 13px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        ">
          <strong>${fullLabel}</strong>: ${value.toFixed(2)}%
        </div>`;
      },
    },
    responsive: [
      {
        breakpoint: 1200,
        options: {
          legend: {
            position: "bottom",
          },
        },
      },
    ],
  };

  return (
    <div className="card">
      <div className="d-flex flex-column flex-md-row justify-content-between p-2">
        <span className="fw-medium" style={{ fontSize: "1rem" }}>
          Route-wise Bus Occupancy
        </span>
        <div className="d-flex flex-column flex-md-row gap-2">
          {showDatePicker && (
            <div className="flex-fill flex-md-grow-0">
              <DatePicker
                selectsRange
                startDate={
                  param?.start_date ? new Date(param.start_date) : null
                }
                endDate={param?.end_date ? new Date(param.end_date) : null}
                onChange={(update) => {
                  const [start, end] = update;
                  setParam((prev) => ({
                    ...prev,
                    start_date: start ? format(start, "yyyy-MM-dd") : "",
                    end_date: end ? format(end, "yyyy-MM-dd") : "",
                    page: 1,
                  }));
                }}
                isClearable
                placeholderText="Select date range"
                className="form-control small-datepicker-input"
                style={{
                  fontSize: "0.5rem",
                }}
                popperProps={{
                  modifiers: [
                    {
                      name: "zIndex",
                      enabled: true,
                      phase: "beforeWrite",
                      fn({ state }) {
                        state.styles.popper.zIndex = 999;
                      },
                    },
                  ],
                }}
              />
            </div>
          )}
          <div className="flex-fill flex-md-grow-0">
            <Select
              options={dateRangeOptions}
              onChange={handleSelectChange}
              placeholder="Select Period"
              className="react-select"
              classNamePrefix="select"
              value={selectedOption}
              styles={{
                menu: (provided) => ({
                  ...provided,
                  zIndex: 999,
                }),
              }}
            />
          </div>
        </div>
      </div>
      <div className="px-2 pb-2">
        {loading ? (
          <div className="text-center my-3">
            <Spinner animation="border" size="sm" />
          </div>
        ) : data?.length == 0 ? (
          <div className="text-center my-2">No Data Found</div>
        ) : error ? (
          <div className="text-center my-3">{error}</div>
        ) : (
          <Chart
            options={options}
            series={series}
            type="polarArea"
            height={250}
          />
        )}
      </div>
    </div>
  );
};

export default RoutewiseBusOccupancy;
